import { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Autocomplete,
	TextField,
	Card,
	CardContent,
	CardActions,
	CardMedia,
	Grid,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	CircularProgress,
} from "@mui/material";
import { Delete, Visibility, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import MDButton from "../../components/MDButton";
import MDBox from "../../components/MDBox";
import apiService from "../../services/api-service";

const FloorPlanManagement = () => {
	const navigate = useNavigate();
	const [areas, setAreas] = useState([]);
	const [selectedArea, setSelectedArea] = useState(null);
	const [floorPlans, setFloorPlans] = useState([]);
	const [loading, setLoading] = useState(false);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [newFloorPlan, setNewFloorPlan] = useState({
		name: "",
		ordering: 0,
		file: null,
	});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		fetchAreas();
	}, []);

	// Auto-select cantiere from localStorage when areas load
	useEffect(() => {
		if (areas.length > 0 && !selectedArea) {
			const storedAreaId = localStorage.getItem("areaSelected");
			if (storedAreaId) {
				const found = areas.find((a) => a.id === parseInt(storedAreaId));
				if (found) setSelectedArea(found);
			}
		}
	}, [areas]);

	useEffect(() => {
		if (selectedArea) {
			fetchFloorPlans();
		} else {
			setFloorPlans([]);
		}
	}, [selectedArea]);

	const fetchAreas = async () => {
		try {
			const response = await apiService.get("/area-management/", {}, {});
			if (response && Array.isArray(response)) {
				setAreas(response.filter((area) => area.yard === true));
			}
		} catch (error) {
			toast.error("Errore caricamento cantieri");
		}
	};

	const fetchFloorPlans = async () => {
		setLoading(true);
		try {
			const response = await apiService.get("/snaglist/floor-plans/", { area_id: selectedArea.id }, {});
			setFloorPlans(response || []);
		} catch (error) {
			toast.error("Errore caricamento planimetrie");
			setFloorPlans([]);
		} finally {
			setLoading(false);
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = () => {
			setNewFloorPlan((prev) => ({
				...prev,
				file: {
					name: `snaglist/floor-plans/${Date.now()}_${file.name}`,
					content: reader.result,
					type: file.type,
					size: file.size,
				},
			}));
		};
		reader.readAsDataURL(file);
	};

	const handleSave = async () => {
		if (!newFloorPlan.name || !newFloorPlan.file || !selectedArea) {
			toast.error("Compila tutti i campi obbligatori");
			return;
		}

		setSaving(true);
		try {
			await apiService.post(
				"/snaglist/floor-plans/",
				{},
				{
					area_id: selectedArea.id,
					name: newFloorPlan.name,
					ordering: newFloorPlan.ordering,
					file: newFloorPlan.file,
				}
			);
			toast.success("Planimetria creata con successo");
			setAddDialogOpen(false);
			setNewFloorPlan({ name: "", ordering: 0, file: null });
			fetchFloorPlans();
		} catch (error) {
			toast.error("Errore creazione planimetria: " + error.message);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (floorPlanId) => {
		if (!window.confirm("Sei sicuro di voler eliminare questa planimetria? Tutti gli snag associati verranno eliminati.")) {
			return;
		}
		try {
			await apiService.delete(`/snaglist/floor-plans/${floorPlanId}/`, {}, {});
			toast.success("Planimetria eliminata");
			fetchFloorPlans();
		} catch (error) {
			toast.error("Errore eliminazione: " + error.message);
		}
	};

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Typography variant="h4" color="primary">
						Gestione Planimetrie
					</Typography>
					<MDButton variant="contained" color="info" onClick={() => navigate("/snag-list")}>
						Torna a Snag List
					</MDButton>
				</Box>

				{/* Area selector */}
				<Box mb={3}>
					<Autocomplete
						options={areas}
						getOptionLabel={(option) => option.name || ""}
						value={selectedArea}
						onChange={(event, newValue) => setSelectedArea(newValue)}
						renderInput={(params) => <TextField {...params} label="Seleziona Cantiere" />}
						sx={{ maxWidth: 400 }}
					/>
				</Box>

				{selectedArea && (
					<Box mb={3}>
						<MDButton
							variant="contained"
							color="success"
							startIcon={<Add />}
							onClick={() => setAddDialogOpen(true)}
						>
							Aggiungi Planimetria
						</MDButton>
					</Box>
				)}

				{/* Floor plan cards */}
				{loading ? (
					<Box display="flex" justifyContent="center" p={4}>
						<CircularProgress />
					</Box>
				) : (
					<Grid container spacing={3}>
						{floorPlans.map((fp) => (
							<Grid item xs={12} sm={6} md={4} key={fp.id}>
								<Card>
									{fp.image_url && (
										<CardMedia
											component="img"
											height="200"
											image={fp.image_url}
											alt={fp.name}
											sx={{ objectFit: "cover" }}
										/>
									)}
									<CardContent>
										<Typography variant="h6">{fp.name}</Typography>
										<Typography variant="body2" color="textSecondary">
											{fp.snag_count || 0} snag
										</Typography>
										<Typography variant="caption" color="textSecondary">
											Ordine: {fp.ordering}
										</Typography>
									</CardContent>
									<CardActions>
										<IconButton
											size="small"
											color="info"
											onClick={() => navigate(`/snag-list/floor-plan/${selectedArea.id}/${fp.id}`)}
											title="Visualizza"
										>
											<Visibility />
										</IconButton>
										<IconButton
											size="small"
											color="error"
											onClick={() => handleDelete(fp.id)}
											title="Elimina"
										>
											<Delete />
										</IconButton>
									</CardActions>
								</Card>
							</Grid>
						))}
						{!loading && floorPlans.length === 0 && selectedArea && (
							<Grid item xs={12}>
								<Typography variant="body1" color="textSecondary" textAlign="center" p={4}>
									Nessuna planimetria trovata per questo cantiere
								</Typography>
							</Grid>
						)}
					</Grid>
				)}

				{/* Add dialog */}
				<Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
					<DialogTitle>Aggiungi Planimetria</DialogTitle>
					<DialogContent>
						<Box display="flex" flexDirection="column" gap={2} mt={1}>
							<TextField
								label="Nome"
								value={newFloorPlan.name}
								onChange={(e) => setNewFloorPlan((prev) => ({ ...prev, name: e.target.value }))}
								fullWidth
								required
							/>
							<TextField
								label="Ordinamento"
								type="number"
								value={newFloorPlan.ordering}
								onChange={(e) => setNewFloorPlan((prev) => ({ ...prev, ordering: parseInt(e.target.value) || 0 }))}
								fullWidth
							/>
							<Button variant="outlined" component="label">
								{newFloorPlan.file ? newFloorPlan.file.name.split("/").pop() : "Carica immagine planimetria"}
								<input type="file" hidden accept="image/*" onChange={handleFileChange} />
							</Button>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setAddDialogOpen(false)}>Annulla</Button>
						<MDButton variant="contained" color="success" onClick={handleSave} disabled={saving}>
							{saving ? <CircularProgress size={20} /> : "Salva"}
						</MDButton>
					</DialogActions>
				</Dialog>
			</Box>
		</DashboardLayout>
	);
};

export default FloorPlanManagement;
